import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import s from './Dashboard.module.css';
import WidgetRenderer, { hasBreakdownLists } from '../Widgets/WidgetRenderer';
import LayoutModal from '../Modal/LayoutModal';
import { useDashboard, A } from '../../context/DashboardContext';
import { useGreeting, useToast } from '../../hooks';
import { BENTO_TEMPLATES, METRICS, CHART_TYPES } from '../../data/catalog';
import Toast from '../Layout/Toast';
import GuidedTour from './Guidedtour/GuidedTour';
//APIS 
import { DashboardLayoutsAPI } from '../../../../api/DashboardLayouts';
import { DashboardMetricsAPI } from '../../../../api/DashboardMetrics';
import { ReportTemplatesAPI } from '../../../../api/ReportTemplates';
import { createEmptyDashboardState, normalizeLayout, parseSavedDashboard } from '../../utils/dashboardPersistence';
import {
  UNCONFIRMED_FILTER_MESSAGE,
  UNSUPPORTED_FILTER_MESSAGE,
  buildBranchScope,
  buildSavedFilters,
  readSavedSelections,
  reconcileSelection,
  scopeConfirmsFilters,
  shouldBlockMetrics,
} from '../../utils/dashboardFilters';
import ScopeNotices from '../Filters/ScopeNotices';
import {
  buildConsolidatedPdf,
  buildExcelExportRequest,
  buildScopeLines,
  downloadBlob,
  filenameFromDisposition,
  safeFileName,
} from '../../utils/consolidatedTable';
import { buildOverviewPdf, downloadPdf, hasPendingMetrics } from '../../utils/overviewPdf';
import { getEventsDateRange, formatYmdForDisplay } from '../../utils/eventDates';
import { canManageBranchGroups } from '../../utils/branchGroupPermissions';
import { useBranchGroups, useEventsCatalog } from '../../hooks/useFilterCatalogs';
import GroupFilter from '../Filters/GroupFilter';
import EventFilter from '../Filters/EventFilter';
import BranchGroupsModal from '../Filters/BranchGroupsModal';
import filterStyles from '../Filters/Filters.module.css';

const DEFAULT_DATE_FILTERS = {
  date_range: 'month',
  date_from: '',
  date_to: '',
};

const DATE_RANGE_OPTIONS = [
  { value: 'today', label: 'Hoy' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'year', label: 'Año' },
  { value: 'custom', label: 'Personalizado' },
];

/* ─── Íconos del resumen y del panel de filtros ─── */
const IcoStore = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9l1.5-5h15L21 9" /><path d="M4 9v11h16V9" /><path d="M9 20v-6h6v6" /><path d="M3 9h18" />
  </svg>
);
const IcoLayers = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" />
  </svg>
);
const IcoTicket = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9V6a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v3a3 3 0 0 0 0 6v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3a3 3 0 0 0 0-6z" /><line x1="13" y1="5" x2="13" y2="19" strokeDasharray="2 2" />
  </svg>
);
const IcoCalendar = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="17" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const IcoChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const IcoClose = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IcoCheck = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M13 4.5 6.5 11 3 7.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcoBolt = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const readStorageJSON = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const getUserBranches = () => {
  const user = readStorageJSON('user');
  return (Array.isArray(user?.branchoffices) ? user.branchoffices : [])
    .map((branch) => {
      const code = branch.code || branch.branchoffice_code || branch.value;
      if (!code) return null;
      return {
        code: String(code),
        name: branch.branchoffice_name || branch.name || branch.label || String(code),
      };
    })
    .filter(Boolean);
};

const branchSummary = (branches, selectedCodes) => {
  if (!branches.length) return 'Sin sucursales';
  if (!selectedCodes.length || selectedCodes.length === branches.length) return 'Todas';
  if (selectedCodes.length === 1) {
    return branches.find(branch => branch.code === selectedCodes[0])?.name || '1 sucursal';
  }
  return `${selectedCodes.length} sucursales`;
};

const metricKey = (layoutId, slotIndex) => `${layoutId}:${slotIndex}`;

const extractDashboardWidgets = (layouts) => layouts.flatMap((layout) => {
  const layoutId = layout.id || layout._id;
  return (layout.slots || [])
    .map((slot, slotIndex) => {
      if (!slot?.metricId || !slot?.chartType) return null;
      return {
        layout_id: layoutId,
        slot_index: slotIndex,
        metricId: slot.metricId,
        chartType: slot.chartType,
        config: slot.config || {},
      };
    })
    .filter(Boolean);
});

const unwrapMetricPayload = (response) => {
  const body = response?.data ?? response;
  return body?.data?.metrics
    ?? body?.data?.results
    ?? body?.data?.widgets
    ?? body?.data
    ?? body?.metrics
    ?? body?.results
    ?? body?.widgets
    ?? body;
};

const isZeroLike = (value) => {
  if (value === 0) return true;
  if (typeof value !== 'string') return false;
  const parsed = Number(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) && parsed === 0;
};

const normalizeMetricEntry = (entry) => {
  const source = entry?.source || entry?.metadata?.source;
  // A metric that cannot be filtered by the selected event is withheld on purpose: no figure, and not an error
  if (source === 'event_filter_not_applicable') {
    return {
      loading: false,
      error: null,
      noData: false,
      blocked: entry?.metadata?.message || 'Esta métrica no se puede filtrar por evento',
      data: undefined,
    };
  }
  // A consolidated table with nothing to show still renders its own empty state (and its explanation), not the generic one
  const isTable = entry?.chartType === 'table' && Array.isArray(entry?.columns);
  const isUnsupportedMetric = source === 'unsupported_metric';
  const hasError = isUnsupportedMetric || Boolean(entry?.error) || entry?.status === 'error' || entry?.success === false;
  const errorMessage = isUnsupportedMetric
    ? 'Métrica no disponible'
    : (entry?.error?.message || entry?.message || 'No se pudo cargar la métrica');
  const isEmptyDataArray = Array.isArray(entry?.data) && entry.data.length === 0;
  const hasNoDataSource = source === 'no_data' || source === 'empty_data';
  const noData = !hasError && !isTable && (
    hasNoDataSource ||
    (isEmptyDataArray && (entry?.value === undefined || isZeroLike(entry?.value) || isZeroLike(entry?.formatted_value)))
  );

  return {
    loading: false,
    error: hasError ? errorMessage : null,
    noData,
    data: entry?.metricData ?? entry?.metric_data ?? entry?.result ?? entry,
  };
};

const normalizeMetricResponse = (response, widgets) => {
  const payload = unwrapMetricPayload(response);
  const map = {};

  const assign = (widget, entry) => {
    if (!widget) return;
    map[metricKey(widget.layout_id, widget.slot_index)] = normalizeMetricEntry(entry);
  };

  const assignByIdentity = (entry, fallbackWidget) => {
    const layoutId = entry?.layout_id ?? entry?.layoutId ?? entry?.layout;
    const slotIndex = entry?.slot_index ?? entry?.slotIndex ?? entry?.slot;
    if (layoutId !== undefined && slotIndex !== undefined) {
      map[metricKey(layoutId, slotIndex)] = normalizeMetricEntry(entry);
      return;
    }
    assign(fallbackWidget, entry);
  };

  if (Array.isArray(payload)) {
    payload.forEach((entry, index) => assignByIdentity(entry, widgets[index]));
    return map;
  }

  if (payload && typeof payload === 'object') {
    const hasDirectIdentity = payload.layout_id !== undefined || payload.layoutId !== undefined;
    if (hasDirectIdentity || widgets.length === 1) {
      assignByIdentity(payload, widgets[0]);
      return map;
    }

    widgets.forEach((widget) => {
      const byMetric = payload[widget.metricId] || payload[widget.metric_id];
      if (byMetric) assign(widget, byMetric);
    });

    Object.entries(payload).forEach(([layoutId, slots]) => {
      if (!slots || typeof slots !== 'object' || Array.isArray(slots)) return;
      Object.entries(slots).forEach(([slotIndex, entry]) => {
        if (!Number.isNaN(Number(slotIndex))) {
          map[metricKey(layoutId, slotIndex)] = normalizeMetricEntry(entry);
        }
      });
    });
  }

  return map;
};

const buildMetricFilters = (dateFilters) => {
  if (dateFilters.date_range !== 'custom') {
    return { date_range: dateFilters.date_range };
  }

  return {
    date_range: 'custom',
    date_from: dateFilters.date_from,
    date_to: dateFilters.date_to,
  };
};

/* ─── Dashboard principal ─────────────────────────────────
   Este archivo define los componentes principales del dashboard:
   - ReplaceConfirm: confirma reemplazo de un widget existente.
   - SlotBadge: muestra la métrica y tipo de gráfico de un slot.
   - ConfirmDialog: modal genérico de confirmación.
   - Btn: botón reutilizable con variantes de estilo.
   - BlockName: nombre editable de un bloque de layout.
   - SlotCell: representa una celda de widget dentro de un bloque.
   - LayoutBlock: bloque de layout que puede ser de tipo columnas o bento.
   - Dashboard: componente principal que controla el modo edición, la barra superior, las acciones y los modales.
*/

/* ─── Confirmación de reemplazo ───────────────────────── */
function ReplaceConfirm({ slot, onConfirm, onCancel }) {
  const metric = METRICS.find(m => m.id === slot?.metricId);
  const chart = CHART_TYPES.find(c => c.id === slot?.chartType);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,15,15,0.5)', backdropFilter: 'blur(3px)', zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 'min(360px,100%)', padding: '22px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
        <div style={{ background: '#f7f7f5', border: '1px solid #e8e8e4', borderRadius: 10, padding: '11px 13px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: metric?.color || '#9f9f98', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{metric?.label || 'Métrica'}</div>
            <div style={{ fontSize: 11, color: '#6b6b66', marginTop: 1 }}>{chart?.label || 'Tipo'}</div>
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, color: '#5046e4', background: '#eeecfd', border: '1px solid #c7d2fe', padding: '2px 8px', borderRadius: 20 }}>Configurado</div>
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>¿Reemplazar este widget?</div>
        <div style={{ fontSize: 13, color: '#6b6b66', lineHeight: 1.6, marginBottom: 18 }}>Este espacio ya tiene una métrica asignada. ¿Quieres reemplazarla?</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '7px 14px', borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer', background: '#fff', border: '1px solid rgba(0,0,0,0.14)', fontFamily: 'inherit' }}>Cancelar</button>
          <button onClick={onConfirm} style={{ padding: '7px 14px', borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer', background: '#5046e4', color: '#fff', border: 'none', fontFamily: 'inherit' }}>Sí, reemplazar</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Badge de slot ─────────────────────────────────────
   Muestra la métrica y tipo de gráfico configurados en un slot.
*/

/* ─── Slot badge ─────────────────────────────────────── */
function SlotBadge({ slot }) {
  const metric = METRICS.find(m => m.id === slot?.metricId);
  const chart = CHART_TYPES.find(c => c.id === slot?.chartType);
  if (!metric || !chart) return null;
  return (
    <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 10, display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.94)', border: '1px solid #e8e8e4', borderRadius: 20, padding: '3px 8px 3px 6px', fontSize: 10, fontWeight: 600, color: '#1a1a18', backdropFilter: 'blur(4px)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', pointerEvents: 'none' }}>
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: metric.color, flexShrink: 0 }} />
      {metric.label} · {chart.label}
    </div>
  );
}

/* ─── Diálogo de confirmación ───────────────────────────
   Modal reutilizable para confirmar acciones del usuario.
*/

/* ─── Confirm dialog ─────────────────────────────────── */
function ConfirmDialog({ title, message, confirmLabel, cancelLabel, extraLabel, onConfirm, onCancel, onExtra, confirmVariant = 'primary' }) {
  const bStyle = v => {
    const base = { padding: '7px 14px', borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer', border: '1px solid transparent', fontFamily: 'inherit', transition: 'all .15s' };
    if (v === 'primary') return { ...base, background: '#5046e4', color: '#fff' };
    if (v === 'danger') return { ...base, background: '#fef2f2', color: '#dc2626', borderColor: '#fecaca' };
    return { ...base, background: '#fff', color: '#1a1a18', borderColor: 'rgba(0,0,0,0.14)' };
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,15,15,0.5)', backdropFilter: 'blur(3px)', zIndex: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 'min(380px,100%)', padding: '22px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 13, color: '#6b6b66', lineHeight: 1.65, marginBottom: 18 }}>{message}</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {extraLabel && <button style={bStyle('secondary')} onClick={onExtra}>{extraLabel}</button>}
          <button style={bStyle('secondary')} onClick={onCancel}>{cancelLabel || 'Cancelar'}</button>
          <button style={bStyle(confirmVariant)} onClick={onConfirm}>{confirmLabel || 'Confirmar'}</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Botón reutilizable ─────────────────────────────────
   Componente simple para acciones con variantes de estilo y tamaño.
*/

/* ─── Inline Btn ─────────────────────────────────────── */
function Btn({ children, v = 'Primary', sz = 'Md', onClick, title, disabled, ...rest }) {
  return (
    <button {...rest} className={`${s.btn} ${s['btn' + v]} ${s['btn' + sz]}`} onClick={onClick} title={title} disabled={disabled}>
      {children}
    </button>
  );
}

/* ─── Nombre de bloque editable ─────────────────────────
   Permite renombrar un bloque cuando el dashboard está en modo edición.
*/

/* ─── Editable block name ────────────────────────────── */
function BlockName({ id, name, editMode }) {
  const { dispatch } = useDashboard();
  const [editing, setEditing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [val, setVal] = useState(name);

  // Keep val in sync if name changes externally
  useEffect(() => { setVal(name); }, [name]);

  const commit = () => {
    setEditing(false);
    const t = val.trim() || name;
    setVal(t);
    if (t !== name) dispatch({ type: A.RENAME_LAYOUT, payload: { id, name: t } });
  };

  if (editing) {
    return (
      <input
        className={`${s.blockName} ${s.blockNameEdit}`}
        value={val} autoFocus
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setVal(name); setEditing(false); } }}
        style={{ width: Math.max(80, val.length * 8 + 20) }}
      />
    );
  }

  // Always allow rename (edit mode or not), show pencil on hover
  return (
    <span
      className={`${s.blockName} ${editMode ? s.blockNameEdit : ''}`}
      onClick={() => editMode && setEditing(true)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={editMode ? 'Clic para renombrar' : name}
      style={{ cursor: editMode ? 'text' : 'default' }}
    >
      {name}
      {editMode && hovered && (
        <span style={{ fontSize: 10, opacity: .45, marginLeft: 4 }}>✎</span>
      )}
    </span>
  );
}

/* ─── Celda de slot ─────────────────────────────────────
   Representa un widget dentro de un bloque. En modo edición permite añadir o reemplazar widgets.
*/

/* ─── Slot cell ──────────────────────────────────────── */
function SlotCell({ slot, slotIdx, layoutId, editMode, blockIdx, onEditSlot, metricState, onExportTable, exportingTable, style = {}, dataTour }) {
  const [replaceTarget, setReplaceTarget] = useState(null);
  const isEmpty = !slot?.metricId || !slot?.chartType;
  const stretchesToGridCell = Boolean(style.gridColumn || style.gridRow);
  const wrapperStyle = stretchesToGridCell ? { height: '100%', ...style } : style;
  // With «Por grupo» / «Por evento» under the chart the card grows to fit them; the fixed height would squash the chart
  const growsWithLists = !stretchesToGridCell && hasBreakdownLists(slot?.metricId, metricState?.data);
  const cardStyle = {
    cursor: editMode ? 'pointer' : 'default',
    position: 'relative',
    ...(stretchesToGridCell ? { height: '100%' } : {}),
    ...(growsWithLists ? { height: 'auto', minHeight: 200 } : {}),
  };
  const emptyStyle = stretchesToGridCell ? { height: '100%' } : {};

  // In view mode: empty slots are completely invisible — render nothing
  if (isEmpty && !editMode) return null;

  const handleClick = () => {
    if (!isEmpty && editMode) {
      setReplaceTarget(true);
    } else if (isEmpty && editMode) {
      onEditSlot(layoutId, slotIdx);
    }
  };

  return (
    <>
      <div style={wrapperStyle} data-tour={dataTour}>
        {!isEmpty ? (
          <div
            className={s.card}
            style={cardStyle}
            onClick={editMode ? handleClick : undefined}
          >
            {editMode && <SlotBadge slot={slot} />}
            <WidgetRenderer
              metricId={slot.metricId}
              chartType={slot.chartType}
              data={metricState?.data}
              // Until the metric has a state it is loading: the widget must never fall back to the sample values of the catalog
              loading={metricState ? metricState.loading : true}
              error={metricState?.error}
              noData={metricState?.noData}
              blocked={metricState?.blocked}
              onExport={onExportTable ? (format) => onExportTable(slot, layoutId, slotIdx, format) : undefined}
              exporting={exportingTable === `${layoutId}:${slotIdx}` ? true : undefined}
              editMode={editMode}
              onEdit={undefined}
            />
          </div>
        ) : (
          // editMode only (view mode returns null above)
          <div className={s.emptySlot} style={emptyStyle} onClick={handleClick}>
            <div className={s.emptyPlus}>+</div>
            <span className={s.emptySlotLabel}>Agregar widget</span>
          </div>
        )}
      </div>

      {replaceTarget && (
        <ReplaceConfirm
          slot={slot}
          onConfirm={() => { setReplaceTarget(false); onEditSlot(layoutId, slotIdx); }}
          onCancel={() => setReplaceTarget(false)}
        />
      )}
    </>
  );
}

/* ─── Bloque de layout ───────────────────────────────────
   Organiza los slots en columnas o en plantilla bento y gestiona arrastrar/soltar para reordenar.
*/

/* ─── Layout block ───────────────────────────────────── */
function LayoutBlock({ layout, index, editMode, onEditSlot, onRequestDelete, metricData, onExportTable, exportingTable, dataTour }) {
  const { dispatch } = useDashboard();
  const [dragOver, setDragOver] = useState(false);
  const isBento = layout.layoutType === 'bento';
  const template = isBento ? BENTO_TEMPLATES.find(t => t.id === layout.bentoTemplateId) : null;
  const UNIT = 170;

  // In view mode: if ALL slots are empty, hide the entire block
  const allEmpty = layout.slots.every(s => !s?.metricId || !s?.chartType);
  if (!editMode && allEmpty) return null; // bento row unit height — tighter

  return (
    <div
      className={`${s.layoutBlock} ${editMode ? s.layoutBlockEdit : ''} ${dragOver ? s.dragOver : ''}`}
      data-tour={dataTour}
      draggable={editMode}
      onDragStart={editMode ? e => e.dataTransfer.setData('text/plain', String(index)) : undefined}
      onDragOver={editMode ? e => { e.preventDefault(); setDragOver(true); } : undefined}
      onDragLeave={editMode ? () => setDragOver(false) : undefined}
      onDrop={editMode ? e => {
        e.preventDefault(); setDragOver(false);
        const from = Number(e.dataTransfer.getData('text/plain'));
        if (from !== index) dispatch({ type: A.REORDER, payload: { fromIdx: from, toIdx: index } });
      } : undefined}
    >
      {/* Block header */}
      <div className={s.blockHead}>
        {editMode && <span className={s.dragGripVisible} title="Arrastrar">⠿</span>}
        <BlockName id={layout.id} name={layout.name} editMode={editMode} />
        {isBento && (
          <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ddd6fe', padding: '2px 7px', borderRadius: 20, marginLeft: 4 }}>Bento</span>
        )}
        {editMode && (
          <div className={s.blockActions}>
            <Btn
              v="Danger"
              sz="Sm"
              /*    onClick={() => {
                   if (window.confirm(`¿Eliminar el bloque "${layout.name}"?`)) {
                     dispatch({ type: A.REMOVE_LAYOUT, payload: layout.id || layout._id });
                   }
                 }} */
              onClick={() => onRequestDelete(layout)}
              title="Eliminar bloque"
            >
              ✕ Eliminar
            </Btn>
          </div>
        )}
      </div>

      {/* Bento */}
      {isBento && template && (() => {
        const hasAny = layout.slots.some(s => s?.metricId && s?.chartType);
        if (!editMode && !hasAny) return null;

        return (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4,minmax(0,1fr))',
            gridTemplateRows: `repeat(3,${UNIT}px)`,
            gap: 8,
          }}>
            {template.slots.map((def, si) => {
              const slot = layout.slots[si];
              const isEmpty = !slot?.metricId || !slot?.chartType;
              // In view mode, empty bento cells collapse to nothing but still hold grid position
              // Use visibility:hidden + height:0 to avoid gaps
              if (isEmpty && !editMode) {
                return (
                  <div
                    key={si}
                    style={{
                      gridColumn: `${def.col}/span ${def.colSpan}`,
                      gridRow: `${def.row}/span ${def.rowSpan}`,
                      visibility: 'hidden',
                      pointerEvents: 'none',
                    }}
                  />
                );
              }
              return (
                <SlotCell
                  key={si} slot={slot} slotIdx={si} layoutId={layout.id}
                  editMode={editMode} blockIdx={index} onEditSlot={onEditSlot}
                  metricState={metricData[metricKey(layout.id, si)]}
                  onExportTable={onExportTable}
                  exportingTable={exportingTable}
                  style={{
                    gridColumn: `${def.col}/span ${def.colSpan}`,
                    gridRow: `${def.row}/span ${def.rowSpan}`,
                  }}
                />
              );
            })}
          </div>
        );
      })()}

      {/* Columns */}
      {!isBento && (() => {
        // In view mode: only count configured slots for the grid column count
        const visibleSlots = editMode
          ? layout.slots
          : layout.slots.filter(s => s?.metricId && s?.chartType);
        const visibleCount = visibleSlots.length;

        // If nothing configured and not in edit mode — show nothing
        if (!editMode && visibleCount === 0) return null;

        const cols = editMode ? layout.cols : Math.min(visibleCount, layout.cols);

        return (
          <div className={s.row} style={{ gridTemplateColumns: `repeat(${cols},minmax(0,1fr))` }}>
            {layout.slots.map((slot, si) => (
              <SlotCell
                key={si} slot={slot} slotIdx={si} layoutId={layout.id}
                editMode={editMode} blockIdx={index} onEditSlot={onEditSlot}
                metricState={metricData[metricKey(layout.id, si)]}
                  onExportTable={onExportTable}
                  exportingTable={exportingTable}
              />
            ))}
          </div>
        );
      })()}
    </div>
  );
}

/* ─── Componente principal del dashboard ─────────────────
   Maneja el estado del dashboard, muestra las barras superior e inferior,
   controla los modales de layout y slot, y muestra mensajes toast.
*/

/* ─── Dashboard ──────────────────────────────────────── */
export default function Dashboard() {
  const { business_code: routeBusinessCode } = useParams();
  const { state, dispatch } = useDashboard();
  const { layouts, editMode, saved, isTemplate } = state;
  const greeting = useGreeting();
  const { toast, show: showToast } = useToast();
  const date = new Date().toLocaleDateString('es-PA', { weekday: 'long', day: 'numeric', month: 'long' });

  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [fabVisible, setFabVisible] = useState(false);
  const [dateFilters, setDateFilters] = useState(DEFAULT_DATE_FILTERS);
  const [metricData, setMetricData] = useState({});
  const [selectedBranchCodes, setSelectedBranchCodes] = useState(() => getUserBranches().map(branch => branch.code));
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [groupsModalOpen, setGroupsModalOpen] = useState(false);
  const [eventDatesNote, setEventDatesNote] = useState(null);
  const [selectionNotice, setSelectionNotice] = useState(null);
  const [metricScope, setMetricScope] = useState(null);
  const [exportingTable, setExportingTable] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [tourSignal, setTourSignal] = useState(0);
  const [tourReady, setTourReady] = useState(false);
  // Panel lateral de filtros: null = cerrado; si no, la sección abierta ('branches' | 'groups' | 'events' | 'range')
  const [filtersPanel, setFiltersPanel] = useState(null);
  const tourActionsRef = useRef({});
  const tourEnteredEditRef = useRef(false);

  const hasLayouts = layouts.length > 0;
  // Un dashboard sin bloques también puede tener cambios pendientes (p. ej. al eliminar el último bloque)
  const isUnsaved = !saved;
  const configuredWidgets = useMemo(() => extractDashboardWidgets(layouts), [layouts]);
  const businessCode = routeBusinessCode || readStorageJSON("business")?.code;
  const currentUser = readStorageJSON("user");
  const userCode = currentUser?.user_code || currentUser?.id;
  const userBranches = useMemo(() => getUserBranches(), []);
  const effectiveBranchCodes = useMemo(
    () => selectedBranchCodes.length ? selectedBranchCodes : userBranches.map(branch => branch.code),
    [selectedBranchCodes, userBranches]
  );
  const toggleBranch = (code) => {
    setSelectedBranchCodes((prev) => {
      if (!prev.includes(code)) return [...prev, code];
      return prev.length > 1 ? prev.filter(item => item !== code) : prev;
    });
  };
  const selectAllBranches = () => setSelectedBranchCodes(userBranches.map(branch => branch.code));

  // Filtros globales de grupos y eventos (catálogos del servidor; un error no bloquea el resto del Overview)
  const groupsCatalog = useBranchGroups(businessCode);
  const eventsCatalog = useEventsCatalog(businessCode);
  const canManageGroups = canManageBranchGroups(currentUser);
  // Mientras /dashboard-metrics/resolve no filtre por grupos y eventos, no se piden ni muestran cifras con esos filtros
  const metricsBlocked = shouldBlockMetrics(selectedGroupIds, selectedEventIds);
  const eventDatesInfo = useMemo(
    () => getEventsDateRange(eventsCatalog.items, selectedEventIds),
    [eventsCatalog.items, selectedEventIds]
  );

  // Las selecciones guardadas pueden apuntar a grupos o eventos que ya no existen: se quitan avisando al usuario
  useEffect(() => {
    const { kept, dropped } = reconcileSelection(selectedGroupIds, groupsCatalog.items.map(group => group.id), groupsCatalog.loaded);
    if (dropped.length > 0) {
      setSelectedGroupIds(kept);
      setSelectionNotice(`Se quitaron ${dropped.length} ${dropped.length === 1 ? 'grupo guardado' : 'grupos guardados'} que ya no están disponibles.`);
    }
  }, [groupsCatalog.loaded, groupsCatalog.items, selectedGroupIds]);

  useEffect(() => {
    const { kept, dropped } = reconcileSelection(selectedEventIds, eventsCatalog.items.map(event => event.id), eventsCatalog.loaded);
    if (dropped.length > 0) {
      setSelectedEventIds(kept);
      setSelectionNotice(`Se quitaron ${dropped.length} ${dropped.length === 1 ? 'evento guardado' : 'eventos guardados'} que ya no están disponibles.`);
    }
  }, [eventsCatalog.loaded, eventsCatalog.items, selectedEventIds]);

  // "Usar fechas del evento": solo cuando el usuario lo pide, nunca al seleccionar el evento
  const handleUseEventDates = () => {
    const info = eventDatesInfo;
    if (!info.range) {
      setEventDatesNote({ kind: 'warn', text: 'Los eventos seleccionados no tienen fechas registradas. El rango no cambió.' });
      return;
    }

    setDateFilters({ date_range: 'custom', date_from: info.range.from, date_to: info.range.to });
    const parts = [`Rango ajustado del ${formatYmdForDisplay(info.range.from)} al ${formatYmdForDisplay(info.range.to)}.`];
    if (info.hasGaps) {
      parts.push(`Los eventos tienen ${info.periods.length} periodos separados: el rango los abarca todos e incluye días intermedios sin actividad de los eventos.`);
    }
    if (info.eventsWithoutDates.length > 0) {
      parts.push(`Sin fechas registradas: ${info.eventsWithoutDates.map(event => event.name).join(', ')}.`);
    }
    setEventDatesNote({ kind: info.hasGaps || info.eventsWithoutDates.length > 0 ? 'warn' : 'info', text: parts.join(' ') });
  };

  useEffect(() => {
    const fn = () => setFabVisible(window.scrollY > 200);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const openAddLayout = useCallback(() => {
    // For simplicity, new layouts are always 2-column with empty slots, but this can be extended to allow choosing layout type and template
    setModal({ mode: 'new' });
  }, []);

  /* ─── Restore the default dashboard (always available, also after editing or saving an empty one) ─── */
  const handleRestoreDefault = () => {
    setConfirm({
      title: 'Restaurar dashboard predeterminado',
      message: 'Se cargará el dashboard predeterminado en lugar del actual. No se guarda hasta que pulses «Guardar dashboard»; si lo descartas, vuelves a tu dashboard guardado.',
      confirmLabel: 'Restaurar',
      cancelLabel: 'Cancelar',
      confirmVariant: 'primary',
      onConfirm: async () => {
        setConfirm(null);
        try {
          const response = await DashboardLayoutsAPI.getDefaultTemplate(localStorage.getItem('x-access-token'));
          const template = response?.data?.data;
          if (!response?.data?.success || !Array.isArray(template?.layouts) || template.layouts.length === 0) throw new Error('Plantilla vacía');
          dispatch({ type: A.APPLY_TEMPLATE, payload: { layouts: template.layouts.map(normalizeLayout) } });
          showToast('Dashboard predeterminado cargado. Guarda para conservarlo');
        } catch (error) {
          showToast('No se pudo cargar el dashboard predeterminado');
        }
      },
      onCancel: () => setConfirm(null),
    });
  };

  /* ─── Smart exit ─── */
  const handleExitEdit = () => {
    if (isUnsaved) {
      setConfirm({
        title: 'Cambios sin guardar',
        message: '¿Quieres guardar el dashboard antes de salir del modo edición? Los espacios vacíos no se mostrarán fuera del modo edición.',
        confirmLabel: 'Guardar y salir',
        cancelLabel: 'Salir sin guardar',
        confirmVariant: 'primary',
        onConfirm: async () => {
          await handleSaveDashboard();
          dispatch({ type: A.EXIT_EDIT });
          setConfirm(null);
        },
        onCancel: () => { dispatch({ type: A.EXIT_EDIT }); setConfirm(null); },
      });
    } else {
      dispatch({ type: A.EXIT_EDIT });
    }
  };

  /* ─── Save dashboard ──── */
  const handleSaveDashboard = async () => {
    if (loadFailed) {
      showToast("No se pudo leer tu dashboard guardado. Recarga la página antes de guardar");
      return;
    }
    try {
      // El dueño del dashboard lo define el servidor a partir de la sesión, no se envía user_code
      const payload = {
        business_code: businessCode,
        dashboard_key: "overview",
        is_default: true,
        filters: buildSavedFilters({
          branchCodes: effectiveBranchCodes,
          metricFilters: buildMetricFilters(dateFilters),
          groupIds: selectedGroupIds,
          eventIds: selectedEventIds,
        }),
        layouts,
      };

      const response = state.dashboardId
        ? await DashboardLayoutsAPI.updateDashboardLayout(state.dashboardId, payload, localStorage.getItem("x-access-token"))
        : await DashboardLayoutsAPI.newDashboardLayout(payload, localStorage.getItem("x-access-token"));

      if (response.data?.success) {
        dispatch({
          type: A.HYDRATE,
          payload: {
            layouts,
            saved: true,
            dashboardId: response.data?.data?._id || state.dashboardId,
          },
        });
        showToast("✓ Dashboard guardado");
      }
    } catch (error) {
      console.error("Error saving dashboard:", error);
      showToast("No se pudo guardar el dashboard");
    }
  };

  // Carga el dashboard guardado del usuario (también se usa para descartar cambios)
  // userCode solo se usa para recargar si cambia la sesión: el servidor identifica al dueño por el token
  const loadDashboard = useCallback(async () => {
    if (!businessCode) return;

    setLoadFailed(false);
    setMetricData({});
    dispatch({ type: A.HYDRATE, payload: createEmptyDashboardState() });
    setDateFilters(DEFAULT_DATE_FILTERS);
    setSelectedBranchCodes(userBranches.map(branch => branch.code));
    setSelectedGroupIds([]);
    setSelectedEventIds([]);
    setEventDatesNote(null);
    setSelectionNotice(null);
    setMetricScope(null);

    try {
      //We call the api to get the dashboard layout for this user/business. We expect to receive the layout data in the response, which we will use to hydrate our state.
      const response = await DashboardLayoutsAPI.getDashboardLayouts({
        business_code: businessCode,
        dashboard_key: "overview",
        token: localStorage.getItem("x-access-token")
      });

      const saved = parseSavedDashboard(response);

      // A saved dashboard is restored as it was, even when it was intentionally left without blocks.
      // Without a saved document the user never configured the overview and the empty state stays.
      if (saved.configured) {
        // Dashboards saved before groups and events existed have no such fields: both selections stay empty
        const selections = readSavedSelections(saved.filters);
        setSelectedGroupIds(selections.groupIds);
        setSelectedEventIds(selections.eventIds);
        if (saved.filters?.date_range) {
          setDateFilters({
            ...DEFAULT_DATE_FILTERS,
            date_range: saved.filters.date_range,
            date_from: saved.filters.date_from || '',
            date_to: saved.filters.date_to || '',
          });
        }
        if (Array.isArray(saved.filters?.branchoffice_code)) {
          const allowedCodes = new Set(userBranches.map(branch => branch.code));
          const savedBranchCodes = saved.filters.branchoffice_code
            .map(String)
            .filter(code => allowedCodes.has(code));
          setSelectedBranchCodes(savedBranchCodes.length ? savedBranchCodes : userBranches.map(branch => branch.code));
        } else {
          setSelectedBranchCodes(userBranches.map(branch => branch.code));
        }
      }

      dispatch({ type: A.HYDRATE, payload: saved.state });
    } catch (error) {
      if (error?.response?.status === 404) {
        dispatch({ type: A.HYDRATE, payload: createEmptyDashboardState() });
        return;
      }

      // Sin lectura confiable del dashboard guardado no se permite guardar, para no pisarlo con uno vacío
      setLoadFailed(true);
      showToast("No se pudo cargar el dashboard");
    } finally {
      setTourReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessCode, dispatch, showToast, userBranches, userCode]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (!businessCode || configuredWidgets.length === 0) {
      setMetricData({});
      setMetricScope(null);
      return;
    }

    // Only if the server could not filter by groups and events (METRICS_SUPPORT_GROUP_EVENT_FILTERS off): no request is
    // made and no figure is shown, so general numbers are never presented as if they belonged to the selection.
    if (metricsBlocked) {
      setMetricScope(null);
      setMetricData((prev) => {
        const next = { ...prev };
        configuredWidgets.forEach((widget) => {
          next[metricKey(widget.layout_id, widget.slot_index)] = {
            loading: false,
            error: null,
            noData: false,
            blocked: UNSUPPORTED_FILTER_MESSAGE,
            data: undefined,
          };
        });
        return next;
      });
      return;
    }

    if (dateFilters.date_range === 'custom' && (!dateFilters.date_from || !dateFilters.date_to)) {
      return;
    }

    let cancelled = false;
    const token = localStorage.getItem("x-access-token");
    const filters = buildMetricFilters(dateFilters);
    const requestedKeys = configuredWidgets.map(widget => metricKey(widget.layout_id, widget.slot_index));

    setMetricData((prev) => {
      const next = { ...prev };
      requestedKeys.forEach((key) => {
        next[key] = { ...(next[key] || {}), loading: true, error: null, blocked: undefined };
      });
      return next;
    });

    // Branches: the individually selected ones, or only the branches of the selected groups when there are groups.
    // Events and dates are separate dimensions combined with AND. The server validates and expands all of it.
    const scopeRequest = buildBranchScope({
      selectedBranchCodes: effectiveBranchCodes,
      groupIds: selectedGroupIds,
    });
    const hasGroupOrEventSelection = selectedGroupIds.length > 0 || selectedEventIds.length > 0;

    const resolveMetrics = async () => {
      try {
        const response = await DashboardMetricsAPI.resolveDashboardMetrics({
          business_code: businessCode,
          branchoffice_code: scopeRequest.branchoffice_code,
          group_ids: scopeRequest.group_ids,
          event_ids: selectedEventIds,
          filters,
          widgets: configuredWidgets,
        }, token);

        if (cancelled) return;

        const scope = response?.data?.scope || null;
        setMetricScope(scope);

        // Figures are only shown if the server confirms it applied the group/event filters: an older server would
        // ignore them and answer with general numbers that look filtered
        if (hasGroupOrEventSelection && !scopeConfirmsFilters(scope)) {
          setMetricData((prev) => {
            const next = { ...prev };
            requestedKeys.forEach((key) => {
              next[key] = { loading: false, error: null, noData: false, blocked: UNCONFIRMED_FILTER_MESSAGE, data: undefined };
            });
            return next;
          });
          return;
        }

        const resolved = normalizeMetricResponse(response, configuredWidgets);
        setMetricData((prev) => {
          const next = { ...prev };
          requestedKeys.forEach((key) => {
            next[key] = resolved[key] || {
              loading: false,
              error: null,
              noData: true,
              data: prev[key]?.data,
            };
          });
          return next;
        });
      } catch (error) {
        if (cancelled) return;
        setMetricScope(null);
        // A refused selection (403/404) must not leave previous figures on screen
        const refused = error?.response?.status === 403 || error?.response?.status === 404;
        const message = refused
          ? (error?.response?.data?.error || 'La selección no está disponible para tu usuario')
          : 'No se pudo cargar la métrica';
        setMetricData((prev) => {
          const next = { ...prev };
          requestedKeys.forEach((key) => {
            next[key] = {
              loading: false,
              error: message,
              noData: false,
              data: refused ? undefined : prev[key]?.data,
            };
          });
          return next;
        });
      }
    };

    resolveMetrics();

    return () => {
      cancelled = true;
    };
  }, [businessCode, configuredWidgets, dateFilters, effectiveBranchCodes, metricsBlocked, selectedGroupIds, selectedEventIds, userBranches]);

  // Exports of a consolidated table. The Excel is built by the server from the same filters and permissions as the screen;
  // the PDF is drawn from the table the server already returned for this screen. Neither can show more than the screen.
  const handleExportTable = async (slot, layoutId, slotIdx, format) => {
    const key = `${layoutId}:${slotIdx}`;
    const token = localStorage.getItem('x-access-token');
    const metricFilters = buildMetricFilters(dateFilters);
    const scopeRequest = buildBranchScope({
      selectedBranchCodes: effectiveBranchCodes,
      groupIds: selectedGroupIds,
    });

    setExportingTable(key);
    try {
      if (format === 'excel') {
        const response = await ReportTemplatesAPI.exportExcel(buildExcelExportRequest({
          businessCode, slot, layoutId, slotIndex: slotIdx, scopeRequest, eventIds: selectedEventIds, metricFilters,
        }), token);
        const filename = filenameFromDisposition(response.headers?.['content-disposition']) || `${safeFileName('ventas_consolidadas')}.xlsx`;
        downloadBlob(response.data, filename);
        showToast('✓ Excel descargado');
      } else {
        const entry = metricData[key]?.data;
        if (!entry || !Array.isArray(entry.columns)) throw new Error('La tabla todavía no está lista para exportar');
        const doc = buildConsolidatedPdf({
          entry,
          scopeLines: buildScopeLines({ scope: metricScope, metricFilters, branchLabel: selectedGroupIds.length > 0 ? '' : branchSummary(userBranches, effectiveBranchCodes) }),
        });
        await downloadPdf(doc, `${safeFileName('ventas_consolidadas')}.pdf`);
        showToast('✓ PDF descargado');
      }
    } catch (error) {
      let message = error?.message || 'No se pudo exportar';
      // With responseType blob a refused export arrives as a blob that holds the JSON error
      if (error?.response?.data && typeof error.response.data.text === 'function') {
        try { message = JSON.parse(await error.response.data.text()).error || message; } catch (_) { /* keep the generic message */ }
      } else if (error?.response?.data?.error) {
        message = error.response.data.error;
      }
      showToast(message);
    } finally {
      setExportingTable(null);
    }
  };

  // PDF de todo el Overview, en tablas. Se arma con las respuestas que el servidor ya dio para esta pantalla.
  const metricsLoading = hasPendingMetrics(layouts, metricData);
  const handleDownloadOverviewPdf = async () => {
    setDownloadingPdf(true);
    try {
      const metricFilters = buildMetricFilters(dateFilters);
      const doc = buildOverviewPdf({
        layouts,
        metricData,
        scopeLines: buildScopeLines({ scope: metricScope, metricFilters, branchLabel: selectedGroupIds.length > 0 ? '' : branchSummary(userBranches, effectiveBranchCodes) }),
      });
      await downloadPdf(doc, `${safeFileName('overview')}_${new Date().toISOString().slice(0, 10)}.pdf`);
      showToast('✓ PDF descargado');
    } catch (error) {
      showToast('No se pudo generar el PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // ── Acciones que el tour dispara al entrar a ciertos pasos ──
  // Entrar a modo edición (solo si hay layouts). Recordamos si fue el tour quien
  // lo activó, para poder revertirlo al terminar sin descartar nada del usuario.
  tourActionsRef.current.enterEditForTour = () => {
    if (!hasLayouts) return false;
    if (!editMode) {
      tourEnteredEditRef.current = true;
      dispatch({ type: A.ENTER_EDIT });
    }
    return true;
  };
  // Volver a vista (usado al retroceder). Solo revierte si el tour activó la edición.
  tourActionsRef.current.exitEditForTour = () => {
    if (!hasLayouts) return false;
    if (tourEnteredEditRef.current && editMode) {
      tourEnteredEditRef.current = false;
      dispatch({ type: A.EXIT_EDIT });
    }
    return true;
  };
  tourActionsRef.current.finishTour = () => {
    if (tourEnteredEditRef.current) {
      tourEnteredEditRef.current = false;
      dispatch({ type: A.EXIT_EDIT });
    }
  };

  const tourSteps = useMemo(() => ([
    {
      title: 'Bienvenido a Overview 👋',
      content: 'Este es tu panel para visualizar las métricas de tu negocio. Te muestro lo esencial en menos de un minuto.',
    },
    {
      target: '[data-tour="branch-filter"]',
      title: 'Filtra por sucursal',
      content: 'Muestra las métricas de una o varias sucursales a la vez. Puedes marcar "Todas".',
      placement: 'bottom',
    },
    {
      target: '[data-tour="date-filter"]',
      title: 'Rango de fechas',
      content: 'Elige el período: hoy, semana, mes, trimestre, año o un rango personalizado.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="edit-btn"]',
      title: 'Modo edición',
      content: 'Desde aquí entras y sales del modo edición para construir tu dashboard.',
      placement: 'bottom',
      action: () => tourActionsRef.current.exitEditForTour?.(),
    },
    {
      target: '[data-tour="edit-banner"]',
      title: 'Construye tu dashboard',
      content: 'En modo edición puedes agregar bloques, reordenarlos arrastrándolos y eliminarlos.',
      placement: 'bottom',
      action: () => tourActionsRef.current.enterEditForTour?.(),
    },
    {
      target: '[data-tour="layout-block"]',
      title: 'Bloques y widgets',
      content: 'Cada bloque agrupa widgets. Toca un espacio vacío para asignarle una métrica y su tipo de gráfico.',
      placement: 'top',
    },
    {
      target: '[data-tour="add-another"]',
      title: 'Agrega más bloques',
      content: 'Añade tantos bloques como necesites para armar la vista que prefieras.',
      placement: 'top',
    },
    {
      target: '[data-tour="save-btn"]',
      title: 'Guarda tus cambios',
      content: 'Cuando termines de editar, guarda el dashboard para conservar tu configuración.',
      placement: 'top',
    },
    {
      target: '[data-tour="add-layout"]',
      title: 'Crea tu primer bloque',
      content: 'Agrega tu primer bloque para empezar a visualizar métricas en tu dashboard.',
      placement: 'top',
    },
    {
      title: '¡Listo! 🎉',
      content: 'Eso es todo. Puedes volver a ver este recorrido cuando quieras desde el botón de ayuda (?) arriba a la derecha.',
    },
  ]), []);


  const allBranchesSelected =
  userBranches.length > 0 &&
  userBranches.every(b => effectiveBranchCodes.includes(b.code));

  // Cerrar el panel de filtros con Escape y bloquear el scroll de fondo mientras está abierto
  useEffect(() => {
    if (!filtersPanel) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setFiltersPanel(null); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [filtersPanel]);

  // Textos de resumen: solo leen el estado de filtros, no lo modifican
  const selectedGroupNames = groupsCatalog.items
    .filter(group => selectedGroupIds.includes(group.id))
    .map(group => group.name);
  const selectedEventNames = eventsCatalog.items
    .filter(event => selectedEventIds.includes(event.id))
    .map(event => event.name);
  const rangeOptionLabel = DATE_RANGE_OPTIONS.find(option => option.value === dateFilters.date_range)?.label || '';
  const rangeSummaryText = dateFilters.date_range === 'custom'
    ? (dateFilters.date_from && dateFilters.date_to
      ? `${formatYmdForDisplay(dateFilters.date_from)} – ${formatYmdForDisplay(dateFilters.date_to)}`
      : 'Elige las fechas')
    : rangeOptionLabel;

  const filterSections = [
    {
      id: 'branches',
      label: 'Sucursales',
      tour: 'branch-filter',
      icon: <IcoStore />,
      value: selectedGroupIds.length > 0 ? 'Según los grupos' : branchSummary(userBranches, effectiveBranchCodes),
      active: selectedGroupIds.length === 0 && !allBranchesSelected,
    },
    {
      id: 'groups',
      label: 'Grupos',
      icon: <IcoLayers />,
      value: selectedGroupIds.length === 0
        ? 'Ninguno'
        : selectedGroupIds.length === 1 ? (selectedGroupNames[0] || '1 grupo') : `${selectedGroupIds.length} grupos`,
      active: selectedGroupIds.length > 0,
    },
    {
      id: 'events',
      label: 'Eventos',
      icon: <IcoTicket />,
      value: selectedEventIds.length === 0
        ? 'Todos'
        : selectedEventIds.length === 1 ? (selectedEventNames[0] || '1 evento') : `${selectedEventIds.length} eventos`,
      active: selectedEventIds.length > 0,
    },
    {
      id: 'range',
      label: 'Rango de fechas',
      tour: 'date-filter',
      icon: <IcoCalendar />,
      value: rangeSummaryText,
      active: false,
    },
  ];

  return (
    <div className={s.shell}>
      {/* Topbar */}
      <header className={s.topbar}>
        <div className={s.logo}>Over<span className={s.logoAccent}>view</span></div>
        <div className={s.topDivider} />
        {/* <span className={s.topTitle}>Smart Pay</span> */}
        <div className={s.topRight}>
          {saved && hasLayouts && !editMode && !isTemplate && (
            <div className={s.savedBadge}><div className={s.savedDot} /> Guardado</div>
          )}
          {isTemplate && hasLayouts && (
            <div className={s.savedBadge} data-testid="template-badge"><div className={s.savedDot} /> Plantilla predeterminada</div>
          )}
          {editMode && <span className={s.editChip}>Modo edición</span>}
          {!isTemplate && (
            <Btn v="Ghost" sz="Sm" data-tour="restore-btn" title="Volver al dashboard predeterminado" onClick={handleRestoreDefault}>↺ Predeterminado</Btn>
          )}
          {hasLayouts && !editMode && (
            <Btn v="Ghost" sz="Sm" data-tour="pdf-btn" title="Descargar el Overview en PDF, en tablas" disabled={metricsLoading || downloadingPdf} onClick={handleDownloadOverviewPdf}>
              {downloadingPdf ? 'Generando…' : 'Descargar PDF '}
            </Btn>
          )}
          {canManageGroups && (
            <Btn v="Ghost" sz="Sm" data-tour="groups-btn" onClick={() => setGroupsModalOpen(true)}>Grupos de sucursales</Btn>
          )}
          {hasLayouts && (
            editMode
              ? <Btn v="Edit" sz="Sm" data-tour="edit-btn" onClick={handleExitEdit}>✓ Salir de edición</Btn>
              : <Btn v="Ghost" sz="Sm" data-tour="edit-btn" onClick={() => dispatch({ type: A.ENTER_EDIT })}>✎ Editar</Btn>
          )}
          <button className={s.helpBtn} title="Ver recorrido guiado" onClick={() => setTourSignal(v => v + 1)} aria-label="Ver recorrido guiado">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
          </button>
        </div>
      </header>

      {/* Edit mode banner */}
      {editMode && (
        <div className={s.editBanner} data-tour="edit-banner">
          <div className={s.editBannerDot} />
          <div>
            <div className={s.editBannerText}>Estás en modo edición </div>
            <div className={s.editBannerHint}>Toca los espacios vacíos para agregar widgets · Arrastra los bloques para reordenarlos</div>
          </div>
        </div>
      )}

      {/* Main */}
      <main className={s.main}>
        <div className={s.pageHead}>
          <div>
            <div className={s.greeting}>{greeting} 👋</div>
            <div className={s.dateMeta}>{date}</div>
          </div>
          {hasLayouts && (
            <div className={s.fsBar} role="group" aria-label="Filtros aplicados al dashboard">
              {filterSections.map(section => (
                <button
                  key={section.id}
                  type="button"
                  className={s.fsCard}
                  data-tour={section.tour}
                  onClick={() => setFiltersPanel(section.id)}
                  aria-haspopup="dialog"
                  title={`Configurar ${section.label.toLowerCase()}`}
                >
                  <span className={s.fsIcon}>
                    {section.icon}
                    {section.active && <span className={s.fsDot} aria-label="Filtro activo" />}
                  </span>
                  <span className={s.fsText}>
                    <span className={s.fsLabel}>{section.label}</span>
                    <span className={s.fsValue}>{section.value}</span>
                  </span>
                  <span className={s.fsChevron}><IcoChevronRight /></span>
                </button>
              ))}
            </div>
          )}
        </div>

        {hasLayouts && metricsBlocked && (
          <div className={filterStyles.blockedNotice} role="status" data-testid="metrics-blocked-notice">
            <span aria-hidden="true">⚠️</span>
            <span>{UNSUPPORTED_FILTER_MESSAGE} Quita los grupos y eventos seleccionados para ver las métricas generales.</span>
          </div>
        )}

        {isTemplate && hasLayouts && (
          <div className={filterStyles.infoNotice} role="status" data-testid="template-notice" style={{ marginBottom: 12 }}>
            Este es el dashboard predeterminado de SmartPay, con tus datos reales. Aún no has guardado uno propio:
            pulsa «Editar» para personalizarlo y «Guardar dashboard» para conservarlo.
          </div>
        )}

        {hasLayouts && <ScopeNotices scope={metricScope} />}

        {selectionNotice && (
          <div className={filterStyles.blockedNotice} role="status" data-testid="selection-notice">
            <span aria-hidden="true">ℹ️</span>
            <span>{selectionNotice}</span>
          </div>
        )}

        {!hasLayouts && (
          <div className={s.emptyDash}>
            <div className={s.emptyIcon}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#5046e4" strokeWidth="1.5" strokeLinecap="round">
                <rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" />
                <rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" />
              </svg>
            </div>
            <div>
              <div className={s.emptyTitle}>Dashboard vacío</div>
              <div className={s.emptyDesc}>Agrega tu primer bloque para visualizar métricas</div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Btn onClick={openAddLayout} data-tour="add-layout">+ Agregar layout</Btn>
              <Btn v="Secondary" onClick={handleRestoreDefault}>↺ Restaurar dashboard predeterminado</Btn>
            </div>
          </div>
        )}

        {layouts.map((layout, i) => (
          <LayoutBlock
            key={layout.id} layout={layout} index={i} editMode={editMode}
            metricData={metricData}
            onExportTable={handleExportTable}
            exportingTable={exportingTable}
            dataTour={i === 0 ? 'layout-block' : undefined}
            onEditSlot={(layoutId, slotIdx) => setModal({ mode: 'editSlot', layoutId, slotIdx })}
            onRequestDelete={(layout) => {
              setConfirm({
                title: '¿Eliminar bloque?',
                message: `Se eliminará "${layout.name}". Este cambio no será definitivo hasta que guardes el dashboard.`,
                confirmLabel: 'Sí, eliminar',
                cancelLabel: 'Cancelar',
                confirmVariant: 'danger',
                onConfirm: () => {
                  dispatch({
                    type: A.REMOVE_LAYOUT,
                    payload: layout.id || layout._id,
                  });

                  setConfirm(null);
                  showToast('Bloque eliminado');
                },
                onCancel: () => setConfirm(null),
              });
            }}
          />
        ))}

        {hasLayouts && editMode && (
          <button className={s.addBtn} onClick={openAddLayout} data-tour="add-another">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Agregar otro layout
          </button>
        )}
      </main>

      {/* Bottom bar */}
      {editMode && isUnsaved && (
        <div className={s.bottomBar}>
          <div className={s.unsavedNote}><div className={s.unsavedDot} /> Cambios sin guardar</div>
          <Btn v="Secondary" sz="Sm" onClick={() => { if (window.confirm('¿Descartar cambios?')) loadDashboard(); }}>Descartar</Btn>
          <Btn v="Primary" sz="Sm" data-tour="save-btn" onClick={handleSaveDashboard}>Guardar dashboard</Btn>
        </div>
      )}

      {/* FAB */}
      {editMode && (
        <button className={`${s.fab} ${fabVisible ? s.fabVisible : ''}`} onClick={openAddLayout} title="Agregar layout">+</button>
      )}

      {/* ─── Panel lateral de filtros ───
          Cada sección se explica y se configura por separado. Usa exactamente los mismos
          estados y handlers que antes: los cambios se aplican al instante. */}
      {filtersPanel && hasLayouts && (
        <div
          className={s.drwOverlay}
          onMouseDown={(event) => { if (event.target === event.currentTarget) setFiltersPanel(null); }}
        >
          <aside className={s.drw} role="dialog" aria-modal="true" aria-labelledby="dash-filters-title">
            <div className={s.drwHead}>
              <div>
                <div id="dash-filters-title" className={s.drwTitle}>Filtros del dashboard</div>
                <div className={s.drwSub}>Definen qué datos muestran todas las métricas del Overview.</div>
              </div>
              <button type="button" className={s.drwClose} onClick={() => setFiltersPanel(null)} aria-label="Cerrar filtros">
                <IcoClose />
              </button>
            </div>

            <div className={s.drwTabs} role="tablist" aria-label="Secciones de filtros">
              {filterSections.map(section => (
                <button
                  key={section.id}
                  type="button"
                  role="tab"
                  aria-selected={filtersPanel === section.id}
                  className={`${s.drwTab} ${filtersPanel === section.id ? s.drwTabOn : ''}`}
                  onClick={() => setFiltersPanel(section.id)}
                >
                  {section.id === 'range' ? 'Rango' : section.label}
                  {section.active && <span className={s.drwTabDot} aria-hidden="true" />}
                </button>
              ))}
            </div>

            <div className={s.drwBody}>
              {/* ── Sucursales ── */}
              {filtersPanel === 'branches' && (
                <section>
                  <div className={s.drwIntro}>
                    <div className={s.drwIntroTitle}>¿De qué sucursales quieres ver datos?</div>
                    <p className={s.drwIntroText}>
                      Marca una o varias sucursales. Las métricas suman las ventas de las que estén marcadas.
                    </p>
                  </div>

                  {selectedGroupIds.length > 0 ? (
                    <div className={s.drwNotice} role="status">
                      Tienes {selectedGroupIds.length === 1 ? 'un grupo seleccionado' : `${selectedGroupIds.length} grupos seleccionados`},
                      así que las métricas usan las sucursales de esos grupos y esta selección no se aplica.{' '}
                      <button type="button" className={s.drwLink} onClick={() => setFiltersPanel('groups')}>
                        Ir a Grupos
                      </button>
                    </div>
                  ) : userBranches.length === 0 ? (
                    <div className={s.drwNotice} role="status">Tu usuario no tiene sucursales asignadas.</div>
                  ) : (
                    <>
                      <div className={s.drwList} role="group" aria-label="Sucursales">
                        <button
                          type="button"
                          className={`${s.drwOpt} ${allBranchesSelected ? s.drwOptOn : ''}`}
                          onClick={selectAllBranches}
                          aria-pressed={allBranchesSelected}
                        >
                          <span className={s.drwCheck}><IcoCheck /></span>
                          <span className={s.drwOptName}>Todas las sucursales</span>
                          <span className={s.drwOptMeta}>{userBranches.length}</span>
                        </button>
                        <div className={s.drwDivider} />
                        {userBranches.map(branch => {
                          const active = effectiveBranchCodes.includes(branch.code);
                          return (
                            <label key={branch.code} className={`${s.drwOpt} ${active ? s.drwOptOn : ''}`}>
                              <input
                                type="checkbox"
                                checked={active}
                                onChange={() => toggleBranch(branch.code)}
                              />
                              <span className={s.drwCheck}><IcoCheck /></span>
                              <span className={s.drwOptName}>{branch.name}</span>
                            </label>
                          );
                        })}
                      </div>
                      <p className={s.drwHint}>Siempre queda al menos una sucursal marcada.</p>
                    </>
                  )}
                </section>
              )}

              {/* ── Grupos ── */}
              {filtersPanel === 'groups' && (
                <section>
                  <div className={s.drwIntro}>
                    <div className={s.drwIntroTitle}>Filtrar por grupos de sucursales</div>
                    <p className={s.drwIntroText}>
                      Un grupo junta varias sucursales bajo un nombre, por ejemplo «Cafeterías». Si eliges uno o más
                      grupos, las métricas usan solo las sucursales de esos grupos y la selección de Sucursales deja de aplicarse.
                    </p>
                  </div>
                  <div className={s.drwComponent}>
                    <GroupFilter
                      groups={groupsCatalog.items}
                      loading={groupsCatalog.loading}
                      error={groupsCatalog.error}
                      onRetry={groupsCatalog.reload}
                      selectedIds={selectedGroupIds}
                      onChange={setSelectedGroupIds}
                      canManage={canManageGroups}
                      onManage={() => setGroupsModalOpen(true)}
                    />
                  </div>
                </section>
              )}

              {/* ── Eventos ── */}
              {filtersPanel === 'events' && (
                <section>
                  <div className={s.drwIntro}>
                    <div className={s.drwIntroTitle}>Filtrar por eventos</div>
                    <p className={s.drwIntroText}>
                      Muestra solo las ventas asociadas a los eventos que elijas. Si no eliges ninguno, se incluyen todas
                      las ventas, con y sin evento. Elegir un evento no cambia el rango de fechas.
                    </p>
                  </div>
                  <div className={s.drwComponent}>
                    <EventFilter
                      events={eventsCatalog.items}
                      loading={eventsCatalog.loading}
                      error={eventsCatalog.error}
                      onRetry={eventsCatalog.reload}
                      selectedIds={selectedEventIds}
                      onChange={(ids) => { setSelectedEventIds(ids); setEventDatesNote(null); }}
                      datesInfo={eventDatesInfo}
                      onUseEventDates={handleUseEventDates}
                      note={eventDatesNote}
                    />
                  </div>
                </section>
              )}

              {/* ── Rango de fechas ── */}
              {filtersPanel === 'range' && (
                <section>
                  <div className={s.drwIntro}>
                    <div className={s.drwIntroTitle}>¿Qué periodo quieres analizar?</div>
                    <p className={s.drwIntroText}>
                      Todas las métricas se calculan dentro de este periodo. Con «Personalizado» eliges la fecha de inicio y de fin.
                    </p>
                  </div>
                  <div className={s.drwRangeGrid} role="radiogroup" aria-label="Rango de fechas">
                    {DATE_RANGE_OPTIONS.map(option => {
                      const active = dateFilters.date_range === option.value;
                      return (
                        <label key={option.value} className={`${s.drwOpt} ${active ? s.drwOptOn : ''}`}>
                          <input
                            type="radio"
                            name="date_range"
                            value={option.value}
                            checked={active}
                            onChange={(event) => setDateFilters((prev) => ({
                              ...prev,
                              date_range: event.target.value,
                            }))}
                          />
                          <span className={`${s.drwCheck} ${s.drwRadio}`}><IcoCheck /></span>
                          <span className={s.drwOptName}>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  {dateFilters.date_range === 'custom' && (
                    <div className={s.drwDates}>
                      <label className={s.drwField}>
                        <span>Desde</span>
                        <input
                          type="date"
                          value={dateFilters.date_from}
                          onChange={(event) => setDateFilters((prev) => ({
                            ...prev,
                            date_from: event.target.value,
                          }))}
                        />
                      </label>
                      <label className={s.drwField}>
                        <span>Hasta</span>
                        <input
                          type="date"
                          value={dateFilters.date_to}
                          onChange={(event) => setDateFilters((prev) => ({
                            ...prev,
                            date_to: event.target.value,
                          }))}
                        />
                      </label>
                    </div>
                  )}
                  {dateFilters.date_range === 'custom' && (!dateFilters.date_from || !dateFilters.date_to) && (
                    <p className={s.drwHint}>Elige ambas fechas para que las métricas se actualicen.</p>
                  )}
                </section>
              )}
            </div>

            <div className={s.drwFoot}>
              <span className={s.drwFootNote}><IcoBolt /> Los cambios se aplican al instante</span>
              <button type="button" className={s.drwDone} onClick={() => setFiltersPanel(null)}>Listo</button>
            </div>
          </aside>
        </div>
      )}

      {/* Modals */}
      {modal?.mode === 'new' && (
        <LayoutModal onClose={() => setModal(null)} onToast={showToast} />
      )}
      {modal?.mode === 'editSlot' && (
        <LayoutModal
          onClose={() => setModal(null)}
          editLayoutId={modal.layoutId}
          editSlotIdx={modal.slotIdx}
          onToast={showToast}
        />
      )}

      {/* Confirm */}
      {groupsModalOpen && canManageGroups && (
        <BranchGroupsModal
          businessCode={businessCode}
          onClose={() => setGroupsModalOpen(false)}
          onChanged={groupsCatalog.reload}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.title} message={confirm.message}
          confirmLabel={confirm.confirmLabel} cancelLabel={confirm.cancelLabel}
          extraLabel={confirm.extraLabel} confirmVariant={confirm.confirmVariant}
          onConfirm={confirm.onConfirm} onCancel={confirm.onCancel} onExtra={confirm.onExtra}
        />
      )}

      {/* Toast */}
      <Toast message={toast.message} visible={toast.visible} type={toast.type} />

      {/* Onboarding guiado */}
      <GuidedTour
        steps={tourSteps}
        storageKey="dash_overview_onboarding_v1"
        autoStart={tourReady && !modal && !confirm}
        startSignal={tourSignal}
        onFinish={() => tourActionsRef.current.finishTour?.()}
      />
    </div>
  );
}